import {
  Box,
  CircularProgress,
  FormControl,
  MenuItem,
  Select,
  Stack,
  Typography,
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";

import type {
  DragonTilePickerProps,
  TilePickerOption,
} from "@/components/DragonTilePicker/types";
import type { ConnectorEvent } from "@/types/connectors";
import type { InnerTileId } from "@/types/dragon-tiles";

import { useDragonTilesLite } from "@/api/dragon-tiles";
import { useDragonTileDisplayName } from "@/features/Connectors/byocTileDisplayName";
import { useConnectorsByDragonTileId } from "@/features/Connectors/hooks/useConnectorsByDragonTileId";
import { resolveActiveInnerTile } from "@/features/DragonTiles/AnalogSequencing/useSelectedInnerTile";
import { UNASSIGNED_DRAGON_TILE } from "@/types/dragon-tiles";

export function DragonTilePicker({
  siteId,
  enabled = true,
  includeConnectors = false,
  allowUnassigned = false,
  defaultInnerTileId,
  onSelectionChange,
  label = "Dragon Tile",
  fullWidth = false,
  width = "460px",
  spacing = 1,
  sx,
}: DragonTilePickerProps) {
  const [selectedValue, setSelectedValue] = useState<string>("");
  const [selectedEventId, setSelectedEventId] = useState<string>("");

  const queryEnabled = !!siteId && enabled;
  const getDisplayName = useDragonTileDisplayName(siteId);

  // Lite: the picker only needs tile names and inner tiles, never the frame
  // refs the composed query pays a site-wide /frames fetch to attach.
  const {
    data: tiles,
    isLoading: isDragonTilesLoading,
    isError: isDragonTilesError,
  } = useDragonTilesLite(siteId, "all", { enabled: queryEnabled });

  const {
    connectorByDragonTileId,
    isLoading: isConnectorsLoading,
    isError: isConnectorsError,
  } = useConnectorsByDragonTileId(siteId, {
    enabled: queryEnabled && includeConnectors,
  });

  const sortedTiles = useMemo(() => {
    if (!tiles) return [];
    // Sort on the displayed name so the BYOC tile lands under the site name
    // rather than under its internal "BYOC" name.
    return [...tiles].sort((a, b) =>
      getDisplayName(a).localeCompare(getDisplayName(b)),
    );
  }, [getDisplayName, tiles]);

  const pickerOptions = useMemo((): TilePickerOption[] => {
    if (!includeConnectors) {
      return sortedTiles.map((tile) => ({
        kind: "basic",
        value: tile.id,
        name: getDisplayName(tile),
      }));
    }

    const basicOptions: TilePickerOption[] = sortedTiles
      .filter((tile) => tile.type !== "connector")
      .map((tile) => ({
        kind: "basic" as const,
        value: tile.id,
        name: getDisplayName(tile),
      }));

    const connectorOptions: TilePickerOption[] = [
      ...connectorByDragonTileId.values(),
    ].map((connector) => ({
      kind: "connector" as const,
      value: connector.dragonTile.id,
      connectorId: connector.connectorId,
      name: connector.tile.name,
    }));

    return [...basicOptions, ...connectorOptions].sort((a, b) =>
      a.name.localeCompare(b.name),
    );
  }, [connectorByDragonTileId, getDisplayName, includeConnectors, sortedTiles]);

  const selectedConnector = connectorByDragonTileId.get(selectedValue);
  const selectedConnectorId = selectedConnector?.connectorId;

  const connectorEvents = useMemo((): ConnectorEvent[] => {
    if (!selectedConnector) return [];
    return selectedConnector.installed.events ?? [];
  }, [selectedConnector]);

  const defaultSelectValue = useMemo(() => {
    if (!defaultInnerTileId) return "";

    const basicOption = pickerOptions.find(
      (option) =>
        option.kind === "basic" &&
        sortedTiles
          .find((tile) => tile.id === option.value)
          ?.inner_tiles.some(
            (innerTile) => innerTile.id === defaultInnerTileId,
          ),
    );
    return basicOption?.value ?? "";
  }, [defaultInnerTileId, pickerOptions, sortedTiles]);

  useEffect(() => {
    if (defaultSelectValue && !selectedValue) {
      setSelectedValue(defaultSelectValue);
    }
  }, [defaultSelectValue, selectedValue]);

  useEffect(() => {
    if (!selectedConnectorId) {
      setSelectedEventId("");
      return;
    }

    setSelectedEventId((current) => {
      if (current && connectorEvents.some((event) => event.id === current)) {
        return current;
      }
      return connectorEvents[0]?.id ?? "";
    });
  }, [connectorEvents, selectedConnectorId]);

  const derivedInnerTileId = useMemo((): InnerTileId | undefined => {
    if (!selectedValue) return undefined;

    if (selectedConnector) {
      const dragonTile =
        sortedTiles.find(
          (tile) => tile.id === selectedConnector.dragonTile.id,
        ) ?? selectedConnector.dragonTile;
      return resolveActiveInnerTile(dragonTile, null)?.id;
    }

    const tile = sortedTiles.find((t) => t.id === selectedValue);
    return tile ? resolveActiveInnerTile(tile, null)?.id : undefined;
  }, [selectedConnector, selectedValue, sortedTiles]);

  const isUnassigned =
    allowUnassigned && selectedValue === UNASSIGNED_DRAGON_TILE;
  const hasInnerTileError =
    !!selectedValue && !isUnassigned && !derivedInnerTileId;
  const hasConnectorWithNoEvents =
    !!selectedConnectorId && connectorEvents.length === 0;
  const hasConnectorEventSelectionError =
    !!selectedConnectorId && connectorEvents.length > 0 && !selectedEventId;
  const hasConnectorEventError =
    hasConnectorWithNoEvents || hasConnectorEventSelectionError;

  const dragonTileId = isUnassigned ? "" : selectedValue;

  useEffect(() => {
    onSelectionChange?.({
      dragonTileId,
      innerTileId: derivedInnerTileId,
      hasError: hasInnerTileError || hasConnectorEventError,
      isConnector: !!selectedConnectorId,
      connectorId: selectedConnectorId ?? undefined,
      selectedEventId: selectedConnectorId
        ? selectedEventId || null
        : undefined,
      isUnassigned,
    });
  }, [
    dragonTileId,
    derivedInnerTileId,
    hasConnectorEventError,
    hasInnerTileError,
    isUnassigned,
    onSelectionChange,
    selectedConnectorId,
    selectedEventId,
  ]);

  const isLoading =
    isDragonTilesLoading || (includeConnectors && isConnectorsLoading);
  const isError =
    isDragonTilesError || (includeConnectors && isConnectorsError);

  const formControlSx = !fullWidth ? { width } : undefined;
  const selectMenuProps = {
    PaperProps: {
      style: {
        maxHeight: 300,
        overflow: "scroll",
      },
    },
  };

  if (isLoading) {
    return (
      <Stack spacing={spacing} sx={sx}>
        <Typography variant="body2">{label}</Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, p: 3 }}>
          <CircularProgress size={20} />
          <Typography variant="body2">Loading Dragon Tiles…</Typography>
        </Box>
      </Stack>
    );
  }

  if (isError) {
    return (
      <Stack spacing={spacing} sx={sx}>
        <Typography variant="body2">{label}</Typography>
        <Typography variant="body2" color="error">
          Failed to load Dragon Tiles. Please try again.
        </Typography>
      </Stack>
    );
  }

  if (pickerOptions.length === 0 && !allowUnassigned) {
    return (
      <Stack spacing={spacing} sx={sx}>
        <Typography variant="body2">{label}</Typography>
        <Typography variant="body2">No Dragon Tiles found</Typography>
      </Stack>
    );
  }

  return (
    <Stack spacing={spacing} sx={sx}>
      <Typography variant="body2">{label}</Typography>
      <FormControl fullWidth={fullWidth} sx={formControlSx}>
        <Select
          value={selectedValue}
          displayEmpty
          onChange={(e) => setSelectedValue(e.target.value)}
          MenuProps={selectMenuProps}
          renderValue={(value) => {
            if (allowUnassigned && value === UNASSIGNED_DRAGON_TILE) {
              return <Typography variant="body2">Unassigned</Typography>;
            }
            if (!value) {
              return <Typography variant="body2">Select tile</Typography>;
            }

            const selectedOption = pickerOptions.find(
              (option) => option.value === value,
            );
            return (
              <Typography variant="body2">
                {selectedOption?.name ?? "Select tile"}
              </Typography>
            );
          }}
        >
          <MenuItem value="" disabled>
            <Typography variant="body2">Select tile</Typography>
          </MenuItem>
          {pickerOptions.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              <Typography variant="body2">{option.name}</Typography>
            </MenuItem>
          ))}
          {allowUnassigned && (
            <MenuItem value={UNASSIGNED_DRAGON_TILE}>
              <Typography variant="body2">Unassigned</Typography>
            </MenuItem>
          )}
        </Select>
      </FormControl>

      {selectedConnectorId && connectorEvents.length > 0 ? (
        <Stack spacing={spacing}>
          <Typography variant="body2">Event</Typography>
          <FormControl fullWidth={fullWidth} sx={formControlSx}>
            <Select
              value={selectedEventId}
              displayEmpty
              onChange={(e) => setSelectedEventId(e.target.value)}
              MenuProps={selectMenuProps}
            >
              <MenuItem value="" disabled>
                <Typography variant="body2">Select event</Typography>
              </MenuItem>
              {connectorEvents.map((event) => (
                <MenuItem key={event.id} value={event.id}>
                  <Typography variant="body2">{event.name}</Typography>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>
      ) : null}

      {hasInnerTileError ? (
        <Typography variant="body2" color="error">
          There's an error with this Dragon Tile. Please choose another.
        </Typography>
      ) : null}
      {hasConnectorEventSelectionError ? (
        <Typography variant="body2" color="error">
          Please select an event for this connector.
        </Typography>
      ) : null}
      {hasConnectorWithNoEvents ? (
        <Typography variant="body2" color="error">
          This connector has no events configured.
        </Typography>
      ) : null}
    </Stack>
  );
}
